"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import { BASE_URL } from "../config/config";

const LecturerContext = createContext();

export const LecturerProvider = ({ children }) => {
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  // 🔹 FETCH
  const fetchLecturers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/lecturers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        setLecturers(data.data || []);
      } else {
        console.error("Fetch failed:", data.message);
      }
    } catch (err) {
      console.error("Network error:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD
  const addLecturer = async (payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.create_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/lecturers`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.status === 201) {
        fetchLecturers();
        return true;
      } else {
        alert(data.message || "Add failed");
        return false;
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Network error");
      return false;
    }
  };

  // 🔹 UPDATE
  const updateLecturer = async (id, payload) => {
    if (!id || !payload) {
      console.error("Payload or ID is undefined!");
      return false;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/lecturers/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        fetchLecturers();
        return true;
      } else {
        alert(data.message || "Update failed");
        return false;
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Network error");
      return false;
    }
  };

  // 🔹 DELETE
  const deleteLecturer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lecturer?")) return;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/lecturers/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: user.id }),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        setLecturers((prev) => prev.filter((l) => l.id !== id));
      } else {
        alert(data.message || "Delete failed");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Delete failed");
    }
  };

  // 🔹 STATUS TOGGLE
  const toggleLecturerStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/lecturers/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, update_audit_id: user.id }),
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        setLecturers((prev) =>
          prev.map((l) => (l.id === id ? { ...l, status: newStatus } : l))
        );
      } else {
        alert(data.message || "Status update failed");
      }
    } catch (err) {
      console.error("Network error:", err);
      alert("Status update failed");
    }
  };

  return (
    <LecturerContext.Provider
      value={{
        lecturers,
        loading,
        fetchLecturers,
        addLecturer,
        updateLecturer,
        deleteLecturer,
        toggleLecturerStatus,
      }}
    >
      {children}
    </LecturerContext.Provider>
  );
};

export const useLecturers = () => useContext(LecturerContext);
